import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as clientsActions from './clients.actions';
import { ClientsService } from './services';
import { mergeMap, map, withLatestFrom, catchError, switchMap } from 'rxjs/operators';
import { ClientsSelectors, AuthSelectors } from '../../../store';
import { ScookieService } from '@store';
import { ClientColumns, ClientFilterOptions, ClientSortOptions } from '@store/models';
import { of } from 'rxjs';

@Injectable()
export class ClientsEffects {

     loadClients$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.LOAD_CLIENTS),
        mergeMap(() => {
            return this.clientsService.getKunder().pipe(
                map(clients =>
                    new clientsActions.LoadClientsSuccess(clients)
                )
            );
        })
    ));

     addClient = createEffect(() => this.actions$.pipe(
        ofType<clientsActions.AddClient>(clientsActions.ADD_CLIENT),
        map(action => action.payload),
        withLatestFrom(this.clientsSelector.allClients$),
        map(([client, allClients]) => {
            const newClients = [...allClients, client];
            return new clientsActions.LoadClientsSuccess(newClients);
        })
    ));

     loadClientColumns$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.LOAD_CLIENT_COLUMNS),
        mergeMap(() => {
            return this.scookieService.getJsonCookie('clientActiveColumns').pipe(
                map((activeColumns: ClientColumns[]) =>
                    new clientsActions.LoadClientColumnsSuccess(activeColumns)
                )
            );
        })
    ));

     loadClientFilter$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.LOAD_CLIENT_COLUMNS),
        mergeMap(() => {
            return this.scookieService.getJsonCookie('clientFilterOptions').pipe(
                map((clientFilter: ClientFilterOptions) =>
                    new clientsActions.SetClientFilter(clientFilter)
                )
            );
        })
    ));

     loadClientSort$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.LOAD_CLIENT_COLUMNS),
        mergeMap(() => {
            return this.scookieService.getJsonCookie('clientSortOptions').pipe(
                map((clientSort: ClientSortOptions) =>
                    new clientsActions.SetClientSort(clientSort)
                )
            );
        })
    ));

     setClientSort$ = createEffect(() => this.actions$.pipe(
        ofType<clientsActions.SetClientSort>(clientsActions.SET_CLIENT_SORT),
        mergeMap(action => {
            const clientSortOptions = action.payload;
            return this.scookieService.saveJsonCookieUnique('clientSortOptions', clientSortOptions);
        })
    ), {dispatch: false});

     setClientFilter$ = createEffect(() => this.actions$.pipe(
        ofType<clientsActions.SetClientFilter>(clientsActions.SET_CLIENT_FILTER),
        withLatestFrom(this.clientsSelector.filterOptions$),
        switchMap(([action, filterOptions]) => {
            const newFilterOptions = {
                ...filterOptions,
                ...action.payload,
            };
            return this.scookieService.saveJsonCookieUnique('clientFilterOptions', newFilterOptions).pipe(
                map(() => {
                    const saUser = newFilterOptions.advanced?.find(f => f.key === 'sa')?.value as string;
                    if (!saUser) { return new clientsActions.LoadFilterKids(''); }
                    return new clientsActions.LoadFilterKids(saUser);
                }),
                catchError(() => of(new clientsActions.LoadFilterKids('')))
            );
        })
    ));

     loadFilterKids$ = createEffect(() => this.actions$.pipe(
        ofType<clientsActions.LoadFilterKids>(clientsActions.LOAD_FILTER_KIDS),
        switchMap(action => {
            const username = action.payload;
            if (!username) { return of(new clientsActions.LoadFilterKidsSuccess(undefined)); }
            return this.clientsService.getCaseKidsByUsername(username).pipe(
                map(kids => new clientsActions.LoadFilterKidsSuccess(kids)),
                catchError(() => of(new clientsActions.LoadFilterKidsSuccess(undefined)))
            );
        })
    ));

     toggleMyClientsFilter$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.TOGGLE_MY_CLIENTS_FILTER),
        withLatestFrom(
            this.authSelector.userId$,
            this.clientsSelector.filterOptions$
        ),
        map(([, userId, filterOptions]) => {
            const myClients = filterOptions.myClients === -1 ? userId : -1;
            return new clientsActions.SetClientFilter({ myClients });
        })
    ));

     toggleActiveClientsFilter$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.TOGGLE_ACTIVE_CLIENTS_FILTER),
        withLatestFrom(this.clientsSelector.filterOptions$),
        map(([, filterOptions]) => {
            const activeClients = !filterOptions.activeClients;
            return new clientsActions.SetClientFilter({ activeClients });
        })
    ));

     setClientColumns$ = createEffect(() => this.actions$.pipe(
        ofType<clientsActions.SetClientColumns>(clientsActions.SET_CLIENT_COLUMNS),
        mergeMap(action => {
            const clientActiveColumns = action.payload;
            return this.scookieService.saveJsonCookieUnique('clientActiveColumns', clientActiveColumns);
        })
    ), {dispatch: false});

     toggleNot469Migreret$ = createEffect(() => this.actions$.pipe(
        ofType(clientsActions.toggleNot469Migreret),
        withLatestFrom(this.clientsSelector.filterOptions$),
        map(([, filterOptions]) => {
            const not469Migreret = !filterOptions.not469Migreret;
            return new clientsActions.SetClientFilter({ not469Migreret });
        })
     ));

    constructor(
        private actions$: Actions,
        private clientsSelector: ClientsSelectors,
        private authSelector: AuthSelectors,
        private clientsService: ClientsService,
        private scookieService: ScookieService
    ) {}

}
